import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, fnViaAlias } from '../lib/apiClient';
import type { Device, SpeedSetting } from '../lib/types';

/**
 * Edit-preset-RPMs modal. Phase 7.5 (#213 deferred follow-up).
 *
 * Backed by `speedSetting` handler — accepts ONE slot per call, with a
 * 1 s gap between calls (firmware pacing constraint, see
 * `speedSetting.js` header). iOS does the same loop on its side; we
 * mirror it exactly so behaviour is identical across clients.
 *
 * The user fills in all four slots, hits Save, and we sequentially
 * write each slot the user actually changed (skipping no-ops to save
 * round-trips on a slow form submit).
 *
 * On error mid-flight we surface which slot failed; previously-written
 * slots stay written — that matches iOS behaviour and is fine for a
 * config write (no transactional guarantee needed).
 */
const SLOTS: Array<keyof SpeedSetting> = ['S1', 'S2', 'S3', 'S4'];

// AquaStar pump motor floor for user-selectable presets is 800 RPM —
// below that the motor stalls / cavitates. S0 (priming) is the only
// slot allowed to dip lower and is firmware-managed, not user-editable.
const RPM_MIN = 800;
// Pump max is 3,450 RPM per the dial. Allow up to 3,600 to give a tiny
// headroom — handler-side validation is the authority anyway.
const RPM_MAX = 3600;

// Pacing between sequential slot writes. Firmware needs ~1 s between
// FN06 frames; 1100 ms gives a comfortable margin.
const PACING_MS = 1100;

export function PresetEditorModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<keyof SpeedSetting, string>>(() => ({
    S1: String(device.speedSetting?.S1 ?? 0),
    S2: String(device.speedSetting?.S2 ?? 0),
    S3: String(device.speedSetting?.S3 ?? 0),
    S4: String(device.speedSetting?.S4 ?? 0),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [progressSlot, setProgressSlot] = useState<keyof SpeedSetting | null>(null);
  const [error, setError] = useState<string | null>(null);

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Esc to close (only when not mid-save — don't drop a partial save).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  function setSlot(slot: keyof SpeedSetting, raw: string) {
    // Strip non-digits so paste-with-spaces / paste-with-commas works.
    const cleaned = raw.replace(/[^\d]/g, '').slice(0, 4);
    setDraft((d) => ({ ...d, [slot]: cleaned }));
  }

  function validate(): { ok: true; values: Record<keyof SpeedSetting, number> } | { ok: false; reason: string } {
    const values = {} as Record<keyof SpeedSetting, number>;
    for (const slot of SLOTS) {
      const n = Number.parseInt(draft[slot], 10);
      if (!Number.isFinite(n) || n < RPM_MIN || n > RPM_MAX) {
        return {
          ok: false,
          reason: `${slot} must be a number between ${RPM_MIN} and ${RPM_MAX} RPM.`,
        };
      }
      values[slot] = n;
    }
    return { ok: true, values };
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const v = validate();
    if (!v.ok) {
      setError(v.reason);
      return;
    }
    // Skip slots whose value didn't change — saves a round-trip per
    // unchanged slot and keeps the firmware quieter.
    const changed = SLOTS.filter(
      (slot) => v.values[slot] !== (device.speedSetting?.[slot] ?? 0),
    );
    if (changed.length === 0) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      for (let i = 0; i < changed.length; i++) {
        const slot = changed[i]!;
        setProgressSlot(slot);
        await fnViaAlias<null>('PUT', '/devices/speed-setting', {
          deviceId: device.deviceId,
          speed: { [slot]: v.values[slot] },
        });
        // Pace between calls (skip after the last write).
        if (i < changed.length - 1) {
          await new Promise((r) => setTimeout(r, PACING_MS));
        }
      }
      setProgressSlot(null);
      onSaved();
      onClose();
    } catch (err) {
      const slot = progressSlot;
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not save preset.';
      setError(
        slot
          ? `${slot} failed: ${msg}${
              changed.indexOf(slot) > 0
                ? ' Earlier slots were saved successfully.'
                : ''
            }`
          : msg,
      );
    } finally {
      setSubmitting(false);
      setProgressSlot(null);
    }
  }

  return (
    <ModalShell title="Edit speed presets" onClose={submitting ? () => {} : onClose}>
      <form onSubmit={onSubmit} noValidate>
        <p className="small text-muted mb-3">
          Each slot stores an RPM value. The pump runs at the slot's RPM
          when you tap the matching preset. Allowed range:{' '}
          <strong>{RPM_MIN.toLocaleString()}–{RPM_MAX.toLocaleString()} RPM</strong>.
        </p>

        {SLOTS.map((slot, i) => (
          <div className="mb-3" key={slot}>
            <label className="form-label small text-muted d-flex justify-content-between">
              <span>
                <strong>{slot}</strong> — preset RPM
              </span>
              {progressSlot === slot ? (
                <span className="text-primary">Saving…</span>
              ) : null}
            </label>
            <div className="input-group">
              <input
                ref={i === 0 ? firstInputRef : undefined}
                type="text"
                inputMode="numeric"
                className="form-control"
                value={draft[slot]}
                onChange={(e) => setSlot(slot, e.target.value)}
                disabled={submitting}
                style={{ fontVariantNumeric: 'tabular-nums' }}
                aria-label={`${slot} RPM`}
              />
              <span className="input-group-text">RPM</span>
            </div>
          </div>
        ))}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting
              ? progressSlot
                ? `Saving ${progressSlot}…`
                : 'Saving…'
              : 'Save presets'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

/**
 * Plain-CSS modal shell — backdrop + centered card. We don't pull in
 * bootstrap.js for one modal; this stays minimal and themed.
 */
export function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          background: 'var(--aqua-surface)',
          color: 'var(--aqua-text)',
          borderRadius: 14,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2"
          style={{
            borderBottom: '1px solid var(--aqua-border)',
            background: 'var(--aqua-surface-muted, transparent)',
          }}
        >
          <h2 className="h6 mb-0" style={{ color: 'var(--aqua-text-strong)' }}>
            {title}
          </h2>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
            style={{ filter: 'var(--aqua-btn-close-filter, none)' }}
          />
        </div>
        <div style={{ padding: '16px 18px' }}>{children}</div>
      </div>
    </div>
  );
}
